import { NavLink } from "react-router-dom";
import { useState } from "react";
import { NAV_GROUPS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { can } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { Logo } from "./Logo";
import { ChevronLeft, ChevronDown } from "lucide-react";

const STORAGE_KEY = "pnc_nav_collapsed";

export function SidebarContent({ collapsed, onToggle, onNavigate }) {
  const { user, tenant } = useAuth();
  const { t } = useI18n();
  const enabledModules = tenant?.modules || [];

  const [closed, setClosed] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")); }
    catch { return new Set(); }
  });
  const toggleGroup = (key) => {
    setClosed((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const isVisible = (it) => {
    if (!can(user?.role, it.perm)) return false;
    if (it.key === "contracts" && enabledModules.length === 0) return false;
    if (it.module && !enabledModules.includes(it.module)) return false;
    return true;
  };

  const renderItem = (it) => (
    <NavLink
      key={it.key}
      to={it.to}
      end={it.to === "/"}
      onClick={onNavigate}
      data-testid={`sidebar-nav-${it.key}`}
      className={({ isActive }) =>
        cn("group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          isActive ? "bg-accent/15 text-white"
            : "text-[hsl(var(--sidebar-foreground))] hover:bg-white/5 hover:text-white",
          collapsed ? "justify-center px-0" : !isTopItem(it) && "pl-9")
      }
      title={t(it.tkey)}
    >
      <it.icon className="h-[18px] w-[18px] shrink-0" />
      {!collapsed && <span className="truncate">{t(it.tkey)}</span>}
    </NavLink>
  );

  return (
    <div className="flex h-full flex-col bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))]">
      <div className="flex h-14 items-center justify-between border-b border-[hsl(var(--sidebar-border))] px-3">
        {!collapsed ? <Logo /> : <Logo compact />}
        {onToggle && (
          <button onClick={onToggle} data-testid="sidebar-toggle"
            className="hidden rounded-md p-1.5 text-[hsl(var(--sidebar-muted))] hover:bg-white/5 hover:text-white lg:block">
            <ChevronLeft className={cn("h-4 w-4 transition-transform", collapsed && "rotate-180")} />
          </button>
        )}
      </div>

      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {NAV_GROUPS.map((group) => {
          const visible = group.items.filter(isVisible);
          if (visible.length === 0) return null;

          // Icon-only (collapsed) mode OR headerless groups render items flat.
          if (collapsed || !group.tkey) return visible.map(renderItem);

          const open = !closed.has(group.key);
          return (
            <div key={group.key} className="pt-2 first:pt-0">
              <button
                onClick={() => toggleGroup(group.key)}
                data-testid={`sidebar-group-${group.key}`}
                className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--sidebar-muted))] transition-colors hover:text-white"
              >
                <span className="flex items-center gap-2">
                  {group.icon && <group.icon className="h-3.5 w-3.5" />}
                  {t(group.tkey)}
                </span>
                <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", !open && "-rotate-90")} />
              </button>
              {open && <div className="mt-0.5 space-y-0.5">{visible.map(renderItem)}</div>}
            </div>
          );
        })}
      </nav>

      {!collapsed && (
        <div className="border-t border-[hsl(var(--sidebar-border))] px-4 py-3 text-[10px] text-[hsl(var(--sidebar-muted))]">
          Tarif: <span className="font-semibold capitalize text-[hsl(var(--sidebar-foreground))]">{tenant?.plan}</span>
        </div>
      )}
    </div>
  );
}

const TOP_KEYS = new Set(["dashboard"]);
function isTopItem(it) {
  return TOP_KEYS.has(it.key);
}
